import React, { useEffect, useRef, useState } from 'react';
import { App, Button, Grid, Modal, Radio, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { fetchSubscriptionPlans, createCheckoutSession } from '../../api/subscriptionApi';
import { showErrorNotification, classifyError, ErrorTypes } from '../../utils/errorHandling';
import useHankoAuth from '../../hooks/useHankoAuth';
import useAppTracking from '../../hooks/useAppTracking';
import { findPlanByName, getPlanDisplay } from './planDisplay';
import { appendUpgradeParams, createUpgradeContext, persistUpgradeIntent } from './upgradeContext';
import { buildCheckoutTrackingProperties, buildUpgradeTrackingProperties } from './subscriptionTracking';
import './MarketDataUpsellModal.css';
import { getInvoiceFormUrl, appendInvoiceOrderParams } from '../../utils/hubspotForms';
import { openInvoiceOrder } from '../../utils/invoiceOrder';
import MarketDataDemo from '../demo/MarketDataDemo';

const HUBSPOT_DEMO_URL = 'https://cal.com/shady-el-tokhy-sl58vi/tendera?utm_source=app&utm_medium=upsell&utm_content=market_data_upsell&metadata[ch]=appi';

const MarketDataUpsellModal = ({ open, onClose }) => {
    const { t } = useTranslation();
    const { notification } = App.useApp();
    const { user } = useHankoAuth();
    const { trackEvent } = useAppTracking();
    const [paidPlan, setPaidPlan] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('online');
    const [subscribing, setSubscribing] = useState(false);
    const [videoModalOpen, setVideoModalOpen] = useState(false);
    const trackedOpenRef = useRef(false);
    // No demo on phones, as on Home: at phone width its text is too small to read.
    const screens = Grid.useBreakpoint();
    const upgradeContext = createUpgradeContext('market_data_upsell');

    useEffect(() => {
        if (!open) return;
        persistUpgradeIntent(upgradeContext);
        let cancelled = false;
        (async () => {
            try {
                const plans = await fetchSubscriptionPlans();
                // Market Data features start at Pro, so this upsell targets Pro specifically -
                // not just "any paid plan," since Monitoring/Intelligence/Enterprise now also exist.
                const paid = findPlanByName(plans, 'Pro');
                if (!cancelled) setPaidPlan(paid || null);
            } catch (error) {
                const errorType = classifyError(error);
                if (errorType !== ErrorTypes.UNAUTHORIZED) {
                    showErrorNotification(error, {
                        title: 'common.error',
                        messageKey: 'common.errors.loadBillingData',
                        t
                    });
                }
            }
        })();
        return () => { cancelled = true; };
    }, [open, t]);

    useEffect(() => {
        if (!open) {
            trackedOpenRef.current = false;
            return;
        }
        if (trackedOpenRef.current) return;
        trackEvent('pro_lock_clicked', buildUpgradeTrackingProperties(upgradeContext, {
            pro_lock_context: 'market_data_upsell_modal',
        }));
        trackEvent('pricing_viewed', buildUpgradeTrackingProperties(upgradeContext, {
            pricing_context: 'market_data_upsell_modal',
        }));
        trackedOpenRef.current = true;
    }, [open, trackEvent, upgradeContext]);

    const handleOpenVideoModal = () => setVideoModalOpen(true);
    const handleCloseVideoModal = () => setVideoModalOpen(false);

    const handleSubscribe = async () => {
        if (!paidPlan) return;
        persistUpgradeIntent(upgradeContext);
        if (paymentMethod === 'invoice') {
            trackEvent('checkout_started', buildCheckoutTrackingProperties(paidPlan, upgradeContext, {
                checkout_context: 'market_data_upsell_modal',
                checkout_type: 'invoice_request',
                payment_method: 'invoice',
            }));
            openInvoiceOrder(
                appendInvoiceOrderParams(appendUpgradeParams(new URL(getInvoiceFormUrl()), upgradeContext), {
                    planName: paidPlan.name,
                    billingInterval: paidPlan.billingInterval,
                }).toString()
            );
            return;
        }
        setSubscribing(true);
        try {
            trackEvent('checkout_started', buildCheckoutTrackingProperties(paidPlan, upgradeContext, {
                checkout_context: 'market_data_upsell_modal',
                checkout_type: 'stripe_checkout',
                payment_method: 'online',
            }));
            const { url } = await createCheckoutSession(paidPlan.id);
            if (user?.id) {
                localStorage.setItem(`tendera:plan-modal-shown:${user.id}`, 'true');
            }
            window.location.href = url;
        } catch (error) {
            const errorType = classifyError(error);
            if (errorType !== ErrorTypes.UNAUTHORIZED) {
                showErrorNotification(error, {
                    title: 'common.error',
                    messageKey: 'common.errors.subscribe',
                    t
                });
            }
        } finally {
            setSubscribing(false);
        }
    };

    const handleBookDemo = () => {
        window.open(HUBSPOT_DEMO_URL, '_blank', 'noopener,noreferrer');
    };

    const paidPlanDisplay = paidPlan ? getPlanDisplay(paidPlan, t) : null;
    const priceLabel = paidPlanDisplay ? paidPlanDisplay.monthlyPriceLabel : '';

    return (
        <>
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={1360}
            rootClassName="market-data-upsell-modal"
            styles={{ body: { padding: 0 } }}
            destroyOnHidden
        >
            <div className="mdu-body">
                <div className="mdu-chip">{t('pages.marketDataUpsell.chip', 'MARKKINADATA')}</div>
                <h2 className="mdu-heading">
                    {t('pages.marketDataUpsell.heading', 'Näe hankinnat ennen kuin ne tulevat kilpailutukseen')}
                </h2>
                <p className="mdu-subheading">
                    {t('pages.marketDataUpsell.subheading', 'Seuraa tehtyjä aloitteita, hyväksyttyjä hankesuunnitelmia ja tulevia investointeja. Näet myös päätökset pienemmistäkin hankinnoista yhdessä näkymässä.')}
                </p>
                <hr className="mdu-divider" />

                <div className="mdu-content">
                    {screens.md && (
                        <div className="mdu-video-col">
                            {/* Coded demo in the interface language (until 10/2026 a Finnish-only screen recording). */}
                            {!videoModalOpen && <MarketDataDemo onExpand={handleOpenVideoModal} />}
                            <p className="mdu-video-caption">
                                {t('pages.marketDataUpsell.videoCaption', 'Katso 30 sek: näin markkinadata toimii')}
                            </p>
                        </div>
                    )}

                    <div className="mdu-questions-col">
                        <div className="mdu-questions-label">
                            {t('pages.marketDataUpsell.haveYouWondered', 'OLETKO MIETTINYT...')}
                        </div>
                        <div className="mdu-question">
                            {t('pages.marketDataUpsell.question1', 'Mitä hankkeita kunnissa suunnitellaan juuri nyt?')}
                        </div>
                        <div className="mdu-question">
                            {t('pages.marketDataUpsell.question2', 'Missä vaiheessa tuleva kilpailutus etenee?')}
                        </div>
                        <div className="mdu-question">
                            {t('pages.marketDataUpsell.question3', 'Mitä on jo ostettu ilman julkista kilpailutusta?')}
                        </div>
                        <div className="mdu-question">
                            {t('pages.marketDataUpsell.question4', 'Kuka voitti, millä hinnalla ja mitä toimittajalta ostettiin?')}
                        </div>
                    </div>
                </div>

                {paidPlan && (
                    <div className="mdu-plan-card">
                        <div className="mdu-plan-top">
                            <div className="mdu-plan-heading">
                                <span className="mdu-plan-name">
                                    {paidPlanDisplay.name}
                                </span>
                                <Tag color="gold">
                                    {t(`pages.settings.billing.planTypes.${paidPlan.planType}`, paidPlan.planType)}
                                </Tag>
                            </div>
                            <div className="mdu-plan-price">
                                <span className="mdu-plan-price-value">{priceLabel}</span>
                                <span className="mdu-plan-price-unit">€/kk</span>
                            </div>
                        </div>
                        <div className="mdu-plan-sub">
                            {t('pages.marketDataUpsell.planSubtext', {
                                defaultValue: 'Sisältää 1–{{max}} käyttäjää · laskutetaan vuosittain {{yearlyPrice}} € + alv',
                                min: 1,
                                max: paidPlanDisplay.maxUsers,
                                yearlyPrice: paidPlanDisplay.yearlyPriceLabel
                            })}
                        </div>
                        <hr className="mdu-divider" />
                        <Radio.Group
                            value={paymentMethod}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                            className="mdu-payment"
                        >
                            <Radio value="online">{t('pages.marketDataUpsell.payCard', 'Maksa kortilla')}</Radio>
                            <Radio value="invoice">{t('pages.marketDataUpsell.payInvoice', 'Maksa laskulla')}</Radio>
                        </Radio.Group>
                        <div className="mdu-actions">
                            <Button
                                type="primary"
                                size="large"
                                loading={subscribing}
                                onClick={handleSubscribe}
                                className="mdu-subscribe"
                            >
                                {t('pages.marketDataUpsell.subscribe', 'Siirry tilaamaan')}
                            </Button>
                            <Button
                                type="default"
                                size="large"
                                onClick={handleBookDemo}
                                className="mdu-book-demo"
                            >
                                {t('pages.marketDataUpsell.bookDemo', 'Varaa esittely')}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Modal>

        <Modal
            open={videoModalOpen}
            onCancel={handleCloseVideoModal}
            footer={null}
            width="min(1100px, 92vw)"
            centered
            destroyOnHidden
            rootClassName="mdu-video-modal mdu-demo-modal"
            styles={{ body: { padding: 0 } }}
        >
            <MarketDataDemo />
        </Modal>
        </>
    );
};

export default MarketDataUpsellModal;
